import {
  Box,
  Typography,
  Chip,
  Divider,
  Collapse,
  IconButton,
  Paper,
  Grid,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { useMemo, useRef, useState } from "react";
import logger from "../../Utils/logger";

// --------------------------------------------------
// Constants
// --------------------------------------------------

const COLORS = {
  primary: "#384F6F",
  secondary: "#718198",
  border: "#E5EAF0",
  lightBorder: "#E8EDF3",
  headerBg: "#FAFBFD",
  headerHover: "#F3F6FA",
  outOfStock: "#C71F1F",
  success: "#00B177",
  offerBlue: "#0091E6",
  offerBlueDark: "#0073B7",
  oldPrice: "#AAAAAA",
};

const EMPTY_VALUE = "—";

// --------------------------------------------------
// Helpers
// --------------------------------------------------

const safe = (value) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY_VALUE;
  }

  return value;
};

const formatPrice = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return EMPTY_VALUE;
  }

  return numericValue.toFixed(2);
};

const formatSectionLabel = (key) => {
  if (!key) {
    return "";
  }

  if (key.toLowerCase() === "info") {
    return "Info";
  }

  return key;
};

// --------------------------------------------------
// Detail Row
// --------------------------------------------------

const DetailRow = ({ label, value }) => {
  const isEmpty =
    value === null ||
    value === undefined ||
    value === "";

  if (isEmpty) {
    return null;
  }

  return (
    <Grid
      container
      alignItems="flex-start"
      sx={{
        py: 1,
      }}
    >
      <Grid item xs={5}>
        <Typography
          fontSize={12}
          fontWeight={500}
          color={COLORS.secondary}
        >
          {label}
        </Typography>
      </Grid>

      <Grid item xs={7}>
        <Typography
          fontSize={12}
          fontWeight={500}
          color={COLORS.primary}
          sx={{
            wordBreak: "break-word",
          }}
        >
          {value}
        </Typography>
      </Grid>
    </Grid>
  );
};

// --------------------------------------------------
// Accordion Header
// --------------------------------------------------

const SectionHeader = ({
  label,
  expanded,
  onClick,
}) => {
  return (
    <>
      <Box
        onClick={onClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          cursor: "pointer",
          bgcolor: COLORS.headerBg,
          transition: "background-color 0.2s ease",
          "&:hover": {
            bgcolor: COLORS.headerHover,
          },
        }}
      >
        <Typography
          fontSize={14}
          fontWeight={600}
          color={COLORS.primary}
        >
          {label}
        </Typography>

        <IconButton
          size="small"
          disableRipple
          sx={{
            p: 0,
            color: COLORS.primary,
          }}
        >
          {expanded ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      {/* Divider should appear only when expanded */}
      {expanded && (
        <Divider
          sx={{
            borderColor: COLORS.border,
          }}
        />
      )}
    </>
  );
};

// --------------------------------------------------
// Dynamic Attribute Section
// --------------------------------------------------

const AttributeSection = ({
  sectionKey,
  sectionData,
  expanded,
  onToggle,
}) => {
  return (
    <Box
      sx={{
       
        mb: 1.5,
        border: `1px solid ${COLORS.lightBorder}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Section Header */}
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          cursor: "pointer",
          transition: "background-color 0.2s ease",

          "&:hover": {
            bgcolor: "#F3F6FA",
          },
        }}
      >
        <Typography
          fontSize={14}
          fontWeight={600}
          color={COLORS.primary}
        >
          {formatSectionLabel(sectionKey)}
        </Typography>

        <IconButton
          size="small"
          disableRipple
          sx={{
            p: 0,
            color: COLORS.primary,
          }}
        >
          {expanded ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      {/* Divider only when expanded */}
      {expanded && (
        <Divider
          sx={{
            borderColor: COLORS.border,
          }}
        />
      )}

      {/* Section Content */}
      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
      >
        <Box
          sx={{
            px: 2,
            py: 1,
          }}
        >
          {Object.entries(sectionData).map(
            ([subKey, value]) => (
              <DetailRow
                key={subKey}
                label={formatSectionLabel(subKey)}
                value={safe(value)}
              />
            )
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
// --------------------------------------------------
// Fallback Info Section
// --------------------------------------------------

const InfoSection = ({
  productDetails,
  expanded,
  onToggle,
}) => {
  return (
    <Box
      sx={{
       
        mb: 2.5,
        border: `1px solid ${COLORS.lightBorder}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <SectionHeader
        label="Info"
        expanded={expanded}
        onClick={onToggle}
      />

      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
      >
        <Box
          sx={{
            px: 2,
            py: 1,
          }}
        >
          <DetailRow
            label="Description"
            value={safe(
              productDetails?.description
            )}
          />

          <DetailRow
            label="Shelf Life"
            value={safe(
              productDetails?.shelfLife
            )}
          />

          <DetailRow
            label="Unit"
            value={`${safe(
              productDetails?.packageWeight
            )} ${safe(
              productDetails?.quantityType
            )}`}
          />

          <DetailRow
            label="HSN Code"
            value={safe(
              productDetails?.hsnCode
            )}
          />

          <DetailRow
            label="Quantity"
            value={`${safe(
              productDetails?.quantityValue
            )} ${safe(
              productDetails?.unit
            )}`}
          />

          <DetailRow
            label="Meal Types"
            value={safe(
              productDetails?.mealType
            )}
          />

          <DetailRow
            label="Brand Name"
            value={safe(
              productDetails?.brandName
            )}
          />

          <DetailRow
            label="Microwave Heating"
            value={
              productDetails
                ?.microwaveHeatingRequired
                ? "YES"
                : "NO"
            }
          />

          <DetailRow
            label="FSSAI License"
            value={safe(
              productDetails?.fssaiNumber
            )}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

// --------------------------------------------------
// Main Component
// --------------------------------------------------

export default function ProductDetails({
  productDetailsData,
}) {
  const data = productDetailsData ?? {};
  const offer = data?.offer_details;

  // --------------------------------------------------
  // Keep last valid attributes
  // --------------------------------------------------

  const attributesRef = useRef([]);

  // --------------------------------------------------
  // Parse attributes_json
  // --------------------------------------------------

  const attributes = useMemo(() => {
    try {
      if (!data?.attributes_json) {
        return attributesRef.current;
      }

      const parsed =
        typeof data.attributes_json === "string"
          ? JSON.parse(data.attributes_json)
          : data.attributes_json;

      if (!Array.isArray(parsed)) {
        return attributesRef.current;
      }

      const validAttributes = parsed.filter(
        (item) =>
          item &&
          typeof item === "object" &&
          Object.keys(item).length > 0
      );

      attributesRef.current = validAttributes;

      return validAttributes;
    } catch (error) {
      logger.error(
        "Failed to parse attributes_json:",
        error
      );

      return attributesRef.current;
    }
  }, [data?.attributes_json]);

  // --------------------------------------------------
  // Dynamic accordion state
  // Default = expanded
  // --------------------------------------------------

  const [expandedSections, setExpandedSections] =
    useState({});

  const toggleSection = (index) => {
    setExpandedSections((previous) => ({
      ...previous,
      [index]: !(previous[index] ?? true),
    }));
  };

  // --------------------------------------------------
  // Fallback Info accordion
  // Default = expanded
  // --------------------------------------------------

  const [expandedInfo, setExpandedInfo] =
    useState(true);

  // --------------------------------------------------
  // Calculate Final Price
  // --------------------------------------------------

  const calculateFinalPrice = (item) => {
    const basePrice = Number(
      item?.mvOfferPrice || 0
    );

    const offerDetails = item?.offer_details;

    // No discount offer
    if (
      !offerDetails ||
      offerDetails.couponType !== "Discount"
    ) {
      return basePrice;
    }

    const flatTypeId = Number(
      offerDetails?.flatTypeId
    );

    const flatAmount = Number(
      offerDetails?.flatAmount || 0
    );

    let discount = 0;

    // Flat amount discount
    if (flatTypeId === 1) {
      discount = flatAmount;
    }

    // Percentage discount
    if (flatTypeId === 2) {
      discount =
        (basePrice * flatAmount) / 100;
    }

    return Math.max(
      basePrice - discount,
      0
    );
  };

  // --------------------------------------------------
  // Offer Chip
  // --------------------------------------------------

  const chipLabel = useMemo(() => {
    if (!offer) {
      return "";
    }

    const flatTypeId = Number(
      offer?.flatTypeId
    );

    const amount = offer?.flatAmount ?? "";

    const amountPrefix =
      flatTypeId === 1 ? "₹" : "";

    const amountSuffix =
      flatTypeId === 2 ? "%" : "";

    let offerSuffix = "";

    if (offer?.couponType === "CashBack") {
      offerSuffix = " Cashback";
    } else if (
      offer?.couponType === "Discount"
    ) {
      offerSuffix = " Off";
    }

    return `${amountPrefix}${amount}${amountSuffix}${offerSuffix}`;
  }, [offer]);

  // --------------------------------------------------
  // Product prices
  // --------------------------------------------------

  const finalPrice = calculateFinalPrice(data);

  const displayPrice =
    data?.isSample === true
      ? finalPrice
      : Number(data?.mvOfferPrice || 0);

  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <Box sx={{ p: 1.25 }}>
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          px: 1.25,
          pb: 1.25,
          pt: 1.25,   
          boxShadow:
            "0px 2px 10px 0px rgba(0, 0, 0, 0.08)",
        }}
      >
        {/* ==========================================
            PRODUCT HEADER
        ========================================== */}

        <Box
          sx={{
            px: 2.5,
            pt: 1.5,
            pb: 1.5,
            boxShadow:
              "0px 2px 10px 0px rgba(0, 0, 0, 0.08)",
          }}
        >
          {/* Product Name */}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography
              fontSize={18}
              fontWeight={600}
              color={COLORS.primary}
            >
              {safe(data?.productName)}
            </Typography>
          </Box>

          {/* Stock */}

          <Typography
            fontSize={12}
            color={COLORS.secondary}
            fontWeight={500}
            sx={{ mb: 1.25 }}
          >
            Item Left . {safe(data?.activeCount)}
          </Typography>

          {/* ========================================
              PRICE
          ======================================== */}

          {!offer ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Typography
                fontSize={14}
                fontWeight={700}
                color={COLORS.primary}
              >
                ₹ {formatPrice(data?.mvOfferPrice)}
              </Typography>

              {Number(data?.activeCount) === 0 && (
                <Chip
                  label="Out of stock"
                  size="small"
                  sx={{
                    bgcolor: COLORS.outOfStock,
                    color: "#FFFFFF",
                    fontSize: 10,
                    fontWeight: 500,
                    borderRadius: "5px",
                  }}
                />
              )}
            </Box>
          ) : (
            <Box
              sx={{
                border: `1px solid ${COLORS.offerBlue}`,
                borderRadius: 2,
                p: 1.5,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Offer Badge */}

              {chipLabel && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    background:
                      `linear-gradient(135deg, ${COLORS.offerBlue}, ${COLORS.offerBlueDark})`,
                    color: "#FFFFFF",
                    fontSize: 10,
                    fontWeight: 700,
                    px: 1.5,
                    py: 0.5,
                    borderRadius:
                      "0 8px 0 12px",
                  }}
                >
                  {chipLabel}
                </Box>
              )}

              {/* App Offer */}

              <Typography
                fontSize={10}
                color={COLORS.success}
                fontWeight={600}
                mb={0.5}
              >
                Only On App
              </Typography>

              {/* Current + MRP */}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Typography
                  fontSize={14}
                  fontWeight={700}
                  color={COLORS.primary}
                >
                  ₹ {formatPrice(displayPrice)}
                </Typography>

                <Typography
                  fontSize={14}
                  color={COLORS.oldPrice}
                  sx={{
                    textDecoration:
                      "line-through",
                  }}
                >
                  ₹ {formatPrice(data?.mrp)}
                </Typography>

                {Number(data?.activeCount) === 0 && (
                  <Chip
                    label="Out of stock"
                    size="small"
                    sx={{
                      bgcolor: COLORS.outOfStock,
                      color: "#FFFFFF",
                      fontSize: 10,
                      fontWeight: 500,
                      borderRadius: "5px",
                    }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* ==========================================
            DYNAMIC ATTRIBUTE SECTIONS
        ========================================== */}

        {attributes.length > 0 ? (
          <Box sx={{ mt: 1.5 }}>
            {attributes.map((item, index) => {
              const sectionKey =
                Object.keys(item)[0];

              const sectionData =
                item?.[sectionKey] ?? {};

              const isExpanded =
                expandedSections[index] ?? true;

              return (
                <AttributeSection
                  key={`${sectionKey}-${index}`}
                  sectionKey={sectionKey}
                  sectionData={sectionData}
                  expanded={isExpanded}
                  onToggle={() =>
                    toggleSection(index)
                  }
                />
              );
            })}
          </Box>
        ) : (
          /* ==========================================
             FALLBACK INFO SECTION
          ========================================== */

          <Box sx={{ mt: 1.5 }}>
            <InfoSection
              productDetails={
                data?.product_details
              }
              expanded={expandedInfo}
              onToggle={() =>
                setExpandedInfo(
                  (previous) => !previous
                )
              }
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
}